import { CSSProperties, ReactNode, useEffect, useState } from 'react';

import { IDataRequest, IResearchTeamMemberDetail } from '@vivli/features/data-requests/infrastructure/interface';
import {
    AdvancedSelectFieldComponent,
    DropdownFormFieldComponent,
    InfoPopoverComponent,
    TextAreaFormFieldComponent,
    TextFormFieldComponent
} from '@vivli/shared/components';
import { Color } from '@vivli/shared/theme';
import { AdminApprovalConstant, DataRequestHintsConstant } from '@vivli/shared/infrastructure/constants';
import { IDropdownMenuItem } from '@vivli/shared/infrastructure/interface';
import {
    useActiveUserContext,
    useDataRequestPermissionsContext,
    useModalApiContext,
    useVivliConfigContext
} from '@vivli/core/infrastructure/context';

import { EnterInstitutionComponent } from '../data-request/data-request-header/components/enter-institution.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import { useFormContext } from 'react-hook-form';
import { ISelectOption } from '@vivli/features/search/infrastructure/interface';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

const containerStyle: CSSProperties = {
    display: 'grid',
    gridAutoRows: 'auto',
    rowGap: '20px',
    textAlign: 'left'
};

const threeColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    columnGap: '20px'
};

const twoColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '20px'
};

const oneColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    columnGap: '20px'
};

const infoPopupStyle: CSSProperties = {
    minHeight: '30px',
    justifyContent: 'flex-end'
};

const orcIdIdHint = (
    <div>
        ORCID iD # is a persistent digital identifier that distinguishes you from every other researcher and, through integration in key
        research workflows such as manuscript and grant submission, supports automated linkages between you and your professional activities
        ensuring that your work is recognized.
    </div>
);

const EmployerCompanyHint = (
    <div>
        Please list only one organization (full name) under which you will carry out the research and which will sign the Data Use
        Agreement. Search for your institution using ROR and select the appropriate institution. If your institution is not in the ROR, then
        manually enter the full name of the institution.
    </div>
);

const conflictsContent = (
    <div>
        <div>
            For each member of the research team, please provide information on financial relationships that could be perceived to influence
            the planning, conduct or interpretation of the proposed research. This should not be limited to financial relationships with the
            study sponsors involved in this initiative and other pharmaceutical or biotechnology companies within the last three years. This
            may include but is not limited to:
        </div>
        <ul>
            <li>Research grants from governments or government agencies</li>
            <li>Other grants or donations</li>
            <li>Funding from employers through employment contracts</li>
            <li>Other contracts, consultancies, honoraria and payments that will be used for the proposed research</li>
            <li>Board memberships</li>
            <li>Patents (planned, pending or issued)</li>
            <li>Royalties - Stocks or shares (including options)</li>
        </ul>
        <div>
            Please also include any other (e.g. non-financial) real or potential conflicts of interest that could be perceived to influence
            the planning, conduct or interpretation of the proposed research. For example, potential biases based on pre-existing personal
            views, academic or commercial competition, personal relationships or institutional affiliations.
        </div>
        <br />
        <div>
            For each conflict, please summarize how real or potential conflicts of interest related to funding of the proposed research,
            other financial relationships or other real or potential conflicts of interest will be managed. For example, through disclosure
            of interests when the research is presented and published.
        </div>
        <br />
        <div>If none please enter "None"</div>
    </div>
);

const vivliPrivateFieldStyle: CSSProperties = {
    backgroundColor: Color.WHITE_TINTED
};

const modalStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'start',
    flexDirection: 'column'
};
const modalHeadingStyle: CSSProperties = {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center'
};

interface TeamMemberFormComponentProps {
    baseFieldName?: string;
    readonly?: boolean;
    isLoading?: boolean;
    children?: ReactNode;
    dataRequest: IDataRequest;
    teamMember: IResearchTeamMemberDetail;
}

export const TeamMemberDetailsFormComponent = ({
    dataRequest,
    baseFieldName,
    readonly,
    children,
    isLoading,
    teamMember
}: TeamMemberFormComponentProps) => {
    const dataRequestPermissions = useDataRequestPermissionsContext();
    const { user, hasPermission } = useActiveUserContext();
    const vivliConfig = useVivliConfigContext();
    const [countryItems, setCountryItems] = useState<IDropdownMenuItem[]>();
    const [countryOptions, setCountryOptions] = useState<ISelectOption[]>();
    const [teamMemberCountry, setTeamMemberCountry] = useState<ISelectOption[]>();

    const userIsAdmin = dataRequest.researchTeam.researchTeamDetails.some((tm) => tm.userId === user?.userId && tm.isAdmin);
    const userIsOwner = dataRequest.userId === user?.userId;
    const userCanEditAllFields = userIsAdmin || userIsOwner;

    const isEmailEditable = dataRequestPermissions.canEditEmailField(teamMember) || userCanEditAllFields;
    const isNonEmailEditable = dataRequestPermissions.canEditNonEmailFields(teamMember) || userCanEditAllFields;
    const labels = AdminApprovalConstant.accessToDataLabels;
    const menuItems = Object.keys(labels).map<IDropdownMenuItem>((key) => {
        return {
            title: labels[key],
            value: key
        };
    });
    const { setValue, getValues } = useFormContext();

    const modalService = useModalApiContext();
    const isReadOnly = !isNonEmailEditable || readonly || isLoading;

    const HandleUpdateFromRor = (index: number, field: string, value: string) => {
        setValue(`${baseFieldName}employerOrAffiliation`, value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
        });
    };

    const handleCountryChange = (options: ISelectOption[]) => {
        if (options && options.length > 0) {
            const countryCode = options[0].value;
            var country = vivliConfig.countries?.find((c) => c.code === countryCode);
            setValue(
                `${baseFieldName}country`,
                { ...country },
                {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true
                }
            );
            setTeamMemberCountry(options);
        }
    };

    const handleInstitutionNameChange = (index, field, value) => {
        const modalId = modalService.custom(
            <div style={{ ...vivliPrivateFieldStyle, padding: '20px', width: '800px' }}>
                <div style={modalStyle}>
                    <div style={modalHeadingStyle}>
                        <h3>Enter Institution</h3>
                        <FontAwesomeIcon
                            style={{ color: 'red', cursor: 'pointer' }}
                            icon={faXmark}
                            onClick={() => modalService.dismiss(modalId)}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'start', width: '100%', alignItems: 'start' }}>
                        <EnterInstitutionComponent
                            onCancel={() => modalService.dismiss(modalId)}
                            index={index}
                            field={field}
                            handleUpdateRow={HandleUpdateFromRor}
                        />
                    </div>
                </div>
            </div>,
            { style: { minWidth: '800px' } }
        );
    };

    useEffect(() => {
        if (vivliConfig?.countries?.length > 0) {
            const countryItems = vivliConfig.countries.map(({ name, code, numberSites }) => ({
                value: { name, code, numberSites },
                title: name
            }));
            const countryOptions: ISelectOption[] = vivliConfig.countries.map((country) => ({
                value: country.code,
                label: country.name
            }));
            setCountryItems(countryItems);
            setCountryOptions(countryOptions);
            if (teamMember?.country) {
                const selectedCountry = countryOptions.find((option) => option.value === teamMember.country.code);
                if (selectedCountry) {
                    setTeamMemberCountry([selectedCountry]);
                }
            }
        }
    }, [vivliConfig]);

    const yearsMenuItems: IDropdownMenuItem[] = [
        { title: '0-1 years', value: '0-1' },
        { title: '1-5 years', value: '1-5' },
        { title: '5-10 years', value: '5-10' },
        { title: '10+ years', value: '10+' }
    ];

    return (
        <div style={containerStyle}>
            <div style={threeColStyle}>
                <TextFormFieldComponent name={`${baseFieldName}firstName`} label={'First Name'} readonly={isReadOnly} />
                <TextFormFieldComponent name={`${baseFieldName}lastName`} label={'Last Name'} readonly={isReadOnly} />
                <TextFormFieldComponent name={`${baseFieldName}orcidId`} label={'ORCID iD'} readonly={isReadOnly} hint={orcIdIdHint} />
            </div>
            <div style={twoColStyle}>
                <TextFormFieldComponent
                    name={`${baseFieldName}email`}
                    label={'Email (editable until user is invited to data request)'}
                    readonly={!isEmailEditable || readonly || isLoading}
                    truncateLabel={false}
                />
                <TextFormFieldComponent
                    name={`${baseFieldName}position`}
                    label={'Position at current organization'}
                    readonly={isReadOnly}
                />
            </div>
            <div style={twoColStyle}>
                <TextFormFieldComponent
                    name={`${baseFieldName}employerOrAffiliation`}
                    label={'Employer, Company, Research Institute, or Primary Affiliation'}
                    readonly={isReadOnly}
                    hint={EmployerCompanyHint}
                    truncateLabel={false}
                    onFocus={(event) => handleInstitutionNameChange(0, 'institutionName', event.target.value)}
                />
                <div data-test-id={`${baseFieldName}-team-member-details-form-country`}>
                    <AdvancedSelectFieldComponent
                        name={`${baseFieldName}country`}
                        label={'Country'}
                        readOnly={isReadOnly}
                        options={countryOptions}
                        selectedOptions={teamMemberCountry}
                        isSearchable={true}
                        onChange={handleCountryChange}
                        error={getValues(`${baseFieldName}country`) ? undefined : 'Select one'}
                        targetStyle={{ backgroundColor: 'white' }}
                    />
                </div>
            </div>
            <div style={oneColStyle}>
                <TextAreaFormFieldComponent
                    name={`${baseFieldName}educationOrQualifications`}
                    label={
                        'Education, including professional qualifications that are relevant to the proposed research and are specific to clinical data analysis.'
                    }
                    readonly={isReadOnly}
                    style={{ marginTop: 10 }}
                    hint={DataRequestHintsConstant.researchTeam.leadResearcherTeamMemberDetail.qualifications}
                    rows={4}
                />
            </div>
            <div style={twoColStyle}>
                <TextAreaFormFieldComponent name={`${baseFieldName}degree`} label={'Name of the degree'} readonly={isReadOnly} rows={2} />
                <TextAreaFormFieldComponent
                    name={`${baseFieldName}educationInstitution`}
                    label={'Institution from where the degree was received'}
                    readonly={isReadOnly}
                    rows={2}
                />
            </div>
            <div style={threeColStyle}>
                <TextFormFieldComponent
                    name={`${baseFieldName}fieldOfStudy`}
                    label={'Discipline'}
                    readonly={isReadOnly}
                    hint={DataRequestHintsConstant.researchTeam.researchTeamDetails[0].discipline}
                />
                <TextFormFieldComponent name={`${baseFieldName}yearReceived`} label={'Year Received'} readonly={isReadOnly} />
                <DropdownFormFieldComponent
                    name={`${baseFieldName}yearsOfSecondaryAnalysis`}
                    label={'How many years of experience with secondary analysis '}
                    items={yearsMenuItems}
                    readonly={isReadOnly}
                />
            </div>
            <InfoPopoverComponent content={conflictsContent} style={infoPopupStyle} />
            <div style={{ position: 'relative' }}>
                <TextAreaFormFieldComponent
                    name={`${baseFieldName}conflictsOfInterest`}
                    label={
                        'Please list any real or potential conflicts of interest and describe how these will be managed. If none, please enter None.'
                    }
                    readonly={isReadOnly}
                />

                {children}
            </div>
            <DropdownFormFieldComponent
                name={`${baseFieldName}accessToData`}
                label={'Access to Data'}
                readonly={!isNonEmailEditable || !hasPermission(PermissionsEnum.DrActAdv) || readonly || isLoading} //TODO PERMFE - redo with permissions+
                items={menuItems}
            />
        </div>
    );
};
